/** Normalise handler names (Dean, 8 Oct 2026): dry-run report → apply; alias editor for the odd cases. legal_head only. */
import { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { getHandlerAliases, normaliseOwnerNames, saveHandlerAliases } from "@/lib/api";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

const sumCounts = (c) => Object.values(c || {}).reduce((a, b) => a + b, 0);

export default function NormaliseNamesDialog({ open, onClose, onApplied }) {
  const [report, setReport] = useState(null);
  const [aliases, setAliases] = useState({});
  const [roster, setRoster] = useState([]);
  const [newAlias, setNewAlias] = useState("");
  const [newTarget, setNewTarget] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setReport(null);
    try {
      const [r, a] = await Promise.all([normaliseOwnerNames(true), getHandlerAliases()]);
      setReport(r); setAliases(a.aliases || {}); setRoster(a.roster || []);
    } catch (e) { toast.error(errorDetailMessage(e, e.message)); }
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);

  const saveAlias = async (next) => {
    setBusy(true);
    try { const r = await saveHandlerAliases(next); setAliases(r.aliases || {}); toast.success("Aliases saved"); await load(); }
    catch (e) { toast.error(errorDetailMessage(e, e.message)); } finally { setBusy(false); }
  };
  const apply = async () => {
    if (!window.confirm(`Rewrite ${report.mappings.length} spelling(s) across ${report.mappings.reduce((n, m) => n + sumCounts(m.counts), 0)} record(s)? This is logged and cannot be undone.`)) return;
    setBusy(true);
    try { const r = await normaliseOwnerNames(false); toast.success(`Normalised — ${r.applied} record(s) updated`); await load(); onApplied?.(); }
    catch (e) { toast.error(errorDetailMessage(e, e.message)); } finally { setBusy(false); }
  };
  const emailOf = (name) => roster.find((p) => p.name === name)?.email || name;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="normalise-names-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wand2 className="w-4 h-4" /> Normalise handler names</DialogTitle>
          <DialogDescription>One person, one name. Legacy handles on cases and diary actions are rewritten to the roster name. Dry run first; apply is logged.</DialogDescription>
        </DialogHeader>
        {!report ? <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div> : (
          <div className="space-y-4 text-xs">
            <div>
              <p className="font-medium mb-1">Will be rewritten ({report.mappings.length})</p>
              {report.mappings.length === 0 ? <p className="italic text-muted-foreground">Nothing to normalise — every stored name is already a roster name.</p> : (
                <table className="w-full border rounded-md" data-testid="normalise-mappings">
                  <thead className="bg-slate-50 text-[10px] uppercase text-slate-500"><tr><th className="text-left px-2 py-1">Stored as</th><th className="text-left px-2 py-1">Becomes</th><th className="text-right px-2 py-1">Cases (handler)</th><th className="text-right px-2 py-1">Cases (legal)</th><th className="text-right px-2 py-1">Actions</th></tr></thead>
                  <tbody className="divide-y">
                    {report.mappings.map((m) => (
                      <tr key={m.from} data-testid={`normalise-row-${m.from}`}>
                        <td className="px-2 py-1 font-mono">{m.from}</td><td className="px-2 py-1 font-medium">{m.to}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{m.counts["collections_cases.handler"] || 0}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{m.counts["collections_cases.legal_handler"] || 0}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{m.counts["legal_calendar_events.owner"] || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div>
              <p className="font-medium mb-1">Odd cases — not on the roster, left as they are ({report.unresolved.length})</p>
              {report.unresolved.length === 0 ? <p className="italic text-muted-foreground">None.</p> : (
                <ul className="flex flex-wrap gap-1.5" data-testid="normalise-unresolved">
                  {report.unresolved.map((u) => (
                    <li key={u.name}>
                      <button type="button" className="px-2 py-0.5 rounded border bg-amber-50 text-amber-900 border-amber-200 cursor-pointer" title="Click to add as an alias" onClick={() => setNewAlias(u.name)} data-testid={`normalise-unresolved-${u.name}`}>
                        {u.name} <span className="text-amber-700">×{sumCounts(u.counts)}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="border rounded-md p-3 space-y-2" data-testid="alias-editor">
              <p className="font-medium">Aliases → person</p>
              <ul className="divide-y">
                {Object.entries(aliases).map(([k, v]) => (
                  <li key={k} className="flex items-center gap-2 py-1" data-testid={`alias-row-${k}`}>
                    <span className="font-mono w-32 truncate">{k}</span><span className="text-slate-400">→</span>
                    <span className="flex-1 truncate">{roster.find((p) => p.email === v || p.name === v)?.name || v}<span className="text-slate-400 ml-1">({v})</span></span>
                    <Button size="sm" variant="ghost" className="h-6 px-1.5 text-rose-600" disabled={busy} onClick={() => { const n = { ...aliases }; delete n[k]; saveAlias(n); }} data-testid={`alias-remove-${k}`}><Trash2 className="w-3 h-3" /></Button>
                  </li>
                ))}
              </ul>
              <div className="flex items-center gap-2">
                <Input value={newAlias} onChange={(e) => setNewAlias(e.target.value)} placeholder="Stored spelling, e.g. Niko" className="h-7 text-xs w-44" data-testid="alias-new-key" />
                <Select value={newTarget} onValueChange={setNewTarget}>
                  <SelectTrigger className="h-7 text-xs flex-1" data-testid="alias-new-target"><SelectValue placeholder="Roster person" /></SelectTrigger>
                  <SelectContent>{roster.map((p) => <SelectItem key={p.email || p.name} value={p.name} className="text-xs">{p.name}{p.email ? ` · ${p.email}` : ""}</SelectItem>)}</SelectContent>
                </Select>
                <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy || !newAlias.trim() || !newTarget} onClick={() => { saveAlias({ ...aliases, [newAlias.trim().toLowerCase()]: emailOf(newTarget) }); setNewAlias(""); setNewTarget(""); }} data-testid="alias-add-btn"><Plus className="w-3 h-3 mr-1" /> Add</Button>
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button variant="outline" onClick={load} disabled={busy} data-testid="normalise-rerun-btn">Re-run dry run</Button>
          <Button className="bg-red-700 hover:bg-red-800" onClick={apply} disabled={busy || !report || report.mappings.length === 0} data-testid="normalise-apply-btn">{busy && <Loader2 className="w-3 h-3 animate-spin mr-1" />} Apply</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
